/*
 * Knuska wishlist.
 *
 * Three things live here: the store, the buttons that read and write it, and
 * the page that renders it.
 *
 * Storage is localStorage, keyed by product handle. No account required, which
 * matters for furniture - the list is most useful to the visitor who has not
 * committed to anything yet, and asking them to register first is asking them
 * to commit. The cost is that the list is per-browser: it does not follow the
 * shopper to their phone. Every read and write is wrapped, because private
 * windows and blocked site data make localStorage throw rather than return
 * nothing, and a wishlist that throws must not take the page down with it.
 *
 * The page holds handles only, never product data. Prices, stock and titles are
 * fetched fresh from the Section Rendering API each time the list is shown, so
 * a product that changed price or sold out since it was saved says so - a cached
 * copy would quietly lie.
 */
(() => {
  const KEY = 'knuska:wishlist';
  const EVENT = 'knuska:wishlist:change';

  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  const Store = {
    read() {
      try {
        const raw = window.localStorage.getItem(KEY);
        const list = raw ? JSON.parse(raw) : [];
        return Array.isArray(list) ? list.filter((h) => typeof h === 'string' && h) : [];
      } catch (e) {
        return [];
      }
    },

    write(list) {
      try {
        window.localStorage.setItem(KEY, JSON.stringify(list));
      } catch (e) {
        /* Private window, or site data blocked. The session still works. */
      }
      document.dispatchEvent(new CustomEvent(EVENT, { detail: { list } }));
    },

    has(handle) {
      return this.read().indexOf(handle) !== -1;
    },

    /* Newest first: a wishlist is a pile, not a queue. */
    add(handle) {
      const list = this.read().filter((h) => h !== handle);
      list.unshift(handle);
      this.write(list);
    },

    remove(handle) {
      this.write(this.read().filter((h) => h !== handle));
    },

    toggle(handle) {
      if (this.has(handle)) {
        this.remove(handle);
        return false;
      }
      this.add(handle);
      return true;
    }
  };

  window.KnuskaWishlist = Store;

  /* Buttons and counts ----------------------------------------------------- */

  /*
   * Every button reads the same store, so one pass keeps a product's heart on
   * the card, on its own page and in a quick-add drawer all saying the same
   * thing - there is no per-button state to fall out of step.
   */
  const sync = () => {
    const list = Store.read();

    document.querySelectorAll('[data-kwl-toggle]').forEach((btn) => {
      const on = list.indexOf(btn.dataset.kwlHandle) !== -1;
      btn.setAttribute('aria-pressed', on ? 'true' : 'false');
      btn.classList.toggle('is-on', on);

      const label = on ? btn.dataset.kwlLabelOn : btn.dataset.kwlLabelOff;
      if (label) {
        btn.setAttribute('aria-label', label);
        btn.setAttribute('title', label);
        const text = btn.querySelector('[data-kwl-text]');
        if (text) text.textContent = label;
      }
    });

    document.querySelectorAll('[data-kwl-count]').forEach((el) => {
      el.textContent = list.length;
      el.hidden = list.length === 0;
    });
  };

  document.addEventListener('click', (event) => {
    const btn = event.target.closest('[data-kwl-toggle]');
    if (!btn || !btn.dataset.kwlHandle) return;
    /* The heart sits inside the card's link, which must not follow. */
    event.preventDefault();
    event.stopPropagation();
    Store.toggle(btn.dataset.kwlHandle);
  });

  document.addEventListener(EVENT, sync);

  /* Another tab changed the list; this one should not disagree with it. */
  window.addEventListener('storage', (event) => {
    if (event.key === KEY) sync();
  });

  /*
   * Cards arrive after load too - quick add, a section re-render in the editor,
   * a tab panel being opened. Rather than have every one of those know about
   * the wishlist, this watches for buttons appearing and syncs them. Batched to
   * a frame, so a large injection costs one pass rather than one per node.
   */
  let queued = false;
  const observer = new MutationObserver((records) => {
    if (queued) return;
    const relevant = records.some((record) =>
      Array.from(record.addedNodes).some(
        (node) => node.nodeType === 1 && (node.matches('[data-kwl-toggle], [data-kwl-count]') || node.querySelector('[data-kwl-toggle], [data-kwl-count]'))
      )
    );
    if (!relevant) return;
    queued = true;
    requestAnimationFrame(() => {
      queued = false;
      sync();
    });
  });

  const start = () => {
    sync();
    observer.observe(document.body, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();

  /* The page ---------------------------------------------------------------- */

  if (!customElements.get('knuska-wishlist')) {
    class KnuskaWishlistPage extends HTMLElement {
      connectedCallback() {
        this.grid = this.querySelector('[data-kwl-grid]');
        this.emptyState = this.querySelector('[data-kwl-empty]');
        this.loader = this.querySelector('[data-kwl-loading]');
        this.total = this.querySelector('[data-kwl-total]');
        if (!this.grid) return;

        this.onChange = () => this.render();
        document.addEventListener(EVENT, this.onChange);
        this.addEventListener('click', this.onClick.bind(this));

        this.render();
      }

      disconnectedCallback() {
        document.removeEventListener(EVENT, this.onChange);
      }

      onClick(event) {
        const remove = event.target.closest('[data-kwl-remove]');
        if (remove) {
          event.preventDefault();
          Store.remove(remove.dataset.kwlHandle);
          return;
        }

        const add = event.target.closest('[data-kwl-add]');
        if (add) {
          event.preventDefault();
          this.addToCart(add);
        }
      }

      /*
       * Re-entrant by design: adding to the cart removes from the list, which
       * fires the change event, which calls this again. The guard lets the pass
       * in flight finish and then runs once more, rather than interleaving two
       * reconciliations over the same grid.
       */
      async render() {
        if (this.busy) {
          this.again = true;
          return;
        }
        this.busy = true;

        try {
          const list = Store.read();
          if (this.total) this.total.textContent = list.length;

          Array.from(this.grid.children).forEach((item) => {
            if (list.indexOf(item.dataset.kwlItem) === -1) item.remove();
          });

          const have = Array.from(this.grid.children).map((item) => item.dataset.kwlItem);
          const missing = list.filter((handle) => have.indexOf(handle) === -1);

          if (missing.length) {
            if (this.loader) this.loader.hidden = false;
            const cards = await Promise.all(missing.map((handle) => this.fetchCard(handle)));
            const gone = [];

            cards.forEach((card, i) => {
              if (card) this.grid.appendChild(card);
              else gone.push(missing[i]);
            });

            if (this.loader) this.loader.hidden = true;

            /*
             * A handle that no longer resolves - deleted, unpublished, renamed -
             * is dropped rather than left to fail again on the next visit. Done
             * in one write after the pass, so it costs one more render and not
             * one per dead handle.
             */
            if (gone.length) {
              Store.write(Store.read().filter((h) => gone.indexOf(h) === -1));
            }
          }

          /* Put the rows back into the order the store holds them in. */
          list.forEach((handle) => {
            const item = this.itemFor(handle);
            if (item) this.grid.appendChild(item);
          });

          const empty = this.grid.children.length === 0;
          if (this.emptyState) this.emptyState.hidden = !empty;
          this.grid.hidden = empty;
          this.classList.toggle('is-empty', empty);
          this.classList.add('is-ready');
        } finally {
          this.busy = false;
          if (this.again) {
            this.again = false;
            this.render();
          }
        }
      }

      itemFor(handle) {
        return Array.from(this.grid.children).find((item) => item.dataset.kwlItem === handle) || null;
      }

      async fetchCard(handle) {
        try {
          const url = `${root}products/${encodeURIComponent(handle)}?section_id=${this.dataset.cardSection}`;
          const response = await fetch(url);
          if (!response.ok) return null;

          const html = await response.text();
          const holder = document.createElement('div');
          holder.innerHTML = html;
          return holder.querySelector('[data-kwl-item]');
        } catch (e) {
          /* Offline or blocked: keep the handle, try again next time. */
          return null;
        }
      }

      async addToCart(btn) {
        const id = btn.dataset.kwlVariant;
        if (!id || btn.disabled) return;

        btn.disabled = true;
        btn.classList.add('is-busy');

        const drawer = document.querySelector('cart-drawer');
        let added = false;

        try {
          /*
           * Built the way the theme's own product form builds it, because the
           * helpers below read what comes back: both updateCartIcon and
           * renderContents want response.sections, and an add that does not ask
           * for sections gets none. Posted as form data with the section list
           * attached - a JSON body returns the line item alone, which is a
           * different shape, and reaching into .sections on it throws.
           */
          let sections = 'cart-icon-bubble';
          if (drawer) {
            const host = drawer.closest('.shopify-section');
            if (host) sections += `,${host.id.replace('shopify-section-', '')}`;
          }

          const body = new FormData();
          body.append('id', id);
          body.append('quantity', 1);
          body.append('sections', sections);
          body.append('sections_url', window.location.pathname);

          const response = await fetch(theme.routes.cartAdd, {
            method: 'POST',
            headers: {
              Accept: 'application/javascript',
              'X-Requested-With': 'XMLHttpRequest'
            },
            body
          });

          const data = await response.json();
          if (!response.ok) throw new Error(data.description || data.message || response.status);

          /*
           * From here the cart already holds it. Everything below is display,
           * and each piece is guarded on its own: a drawer that fails to render
           * must not report the add as failed, leave the row on the list, or
           * tempt the shopper into adding it twice.
           */
          added = true;

          try {
            const ProductForm = customElements.get('product-form');
            if (ProductForm && typeof ProductForm.updateCartIcon === 'function') {
              ProductForm.updateCartIcon(data);
            }
          } catch (e) { /* the count is stale until the next page load */ }

          try {
            if (drawer && typeof drawer.renderContents === 'function') {
              drawer.renderContents(data, theme.settings && theme.settings.afterAtc === 'drawer');
            }
          } catch (e) { /* the drawer refreshes when it is next opened */ }

          /*
           * Dispatched before the row goes, while the button is still in the
           * document - an event from a detached node reaches no listener.
           */
          btn.dispatchEvent(new CustomEvent('on:cart:add', {
            bubbles: true,
            detail: { variantId: Number(id) }
          }));
        } catch (e) {
          btn.classList.add('is-error');
          setTimeout(() => btn.classList.remove('is-error'), 2000);
        } finally {
          btn.disabled = false;
          btn.classList.remove('is-busy');
          /*
           * In the cart, so off the list - and last, so it happens on every
           * path where the add actually went through.
           */
          if (added) Store.remove(btn.dataset.kwlHandle);
        }
      }
    }

    customElements.define('knuska-wishlist', KnuskaWishlistPage);
  }
})();
